import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { HStack, Image, Text, Tooltip, useDisclosure, VStack } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import ModalSideBarMenu from '../../../../components/ModalSideBarMenu';
import LoadingSpinner from '../../../../components/Spinner';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { CypressIds } from '../../../../constants/cypressIds';
import { InsuredProfilePageDuskIds } from '../../../../constants/duskIds';
import { selectCurrentRecordId } from '../../../../redux/modules/insuredProfilePage';
import { useInsuredData, useRefreshInsured, useUpdateInsuredData } from '../../../../services/insured-service';
import { getFormattedPhoneNumber, removePhoneNumberFormat } from '../../../../utils/formatPhoneUtil';
import { pruneObject } from '../../../../utils/objectUtil';

import OverallStatusBar from './OverallStatusBar';
import { insuredProfileModalMenuOptions } from './util';

const InsuredProfileHeader = ({ insuredId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const currentRecordId = useSelector(selectCurrentRecordId);

  const { data: insured, isLoading } = useInsuredData(insuredId);
  const { mutate: refreshInsured } = useRefreshInsured({ insuredId, recordId: currentRecordId });
  const { mutate: updateInsured } = useUpdateInsuredData(insuredId);

  const getDefaultValues = () => ({
    insured_company_name: insured?.insuredCompanyName,
    external_id: insured?.externalId,
    insured_phone: getFormattedPhoneNumber(insured?.insuredPhone),
    insured_phone2: getFormattedPhoneNumber(insured?.insuredPhone2),
    insured_type: insured?.insuredType,
    insured_description: insured?.insuredDescription,
    guarantor_name: insured?.guarantorName,
    legal_name: insured?.legalName,

    insured_address: insured?.insuredAddress,
    insured_state: insured?.insuredState,
    insured_city: insured?.insuredCity,
    insured_zip: insured?.insuredZip,
    country: insured?.country,

    insured_contact_name: insured?.insuredContactName,
    insured_email: insured?.insuredEmail,
    insured_cc: insured?.insuredCc
  });

  const onSubmit = async data => {
    const pruned = pruneObject({
      ...data,
      insured_phone: removePhoneNumberFormat(data.insured_phone),
      insured_phone2: removePhoneNumberFormat(data.insured_phone2)
    });

    // always add in default_requirement_template_id after prune (i.e., resetting to null)
    pruned.default_requirement_template_id = data?.default_requirement_template_id ?? null;

    updateInsured(pruned);
  };

  const useFormMethods = useForm(
    { defaultValues: getDefaultValues() }
  );

  useEffect(() => {
    useFormMethods.reset(getDefaultValues());
  }, [insured]);

  const options = useMemo(() => insuredProfileModalMenuOptions(insured, useFormMethods, onSubmit), [insured]);

  const insuredIntegrations = useMemo(() => insured?.insuredIntegrations ?? [], insured);

  if (isLoading) return <LoadingSpinner />;

  return (
    <>
      <VStack alignItems="center" justifyContent="center" mb={12} spacing={5}>
        <Text
          _hover={{ cursor: 'pointer' }}
          color="primaryScale.600"
          data-analytics-id={analyticsIds.insuredProfilePage.insuredDetailsModal.insuredNameHeaderButton}
          data-cy={CypressIds.insuredProfileInsuredHeaderName}
          dusk={InsuredProfilePageDuskIds.insuredHeaderName}
          textStyle="displayXs"
          onClick={onOpen}
        >
          {insured?.insuredCompanyName}
        </Text>
        <OverallStatusBar insuredId={insuredId} />

        <HStack>
          {insuredIntegrations.filter(insuredIntegration => insuredIntegration.integration.integrationType.thumbnailUrl)
            .map(insuredIntegration => (
              <Tooltip key={insuredIntegration.insuredId} label={insuredIntegration.externalId}>
                <Image
                  alt="integration image"
                  height="20px"
                  objectFit="contain"
                  src={insuredIntegration.integration.integrationType.thumbnailUrl}
                  width="20px"
                />
              </Tooltip>
            ))}
        </HStack>

      </VStack>
      <ModalSideBarMenu
        isOpen={isOpen}
        options={options}
        refreshFunction={onSuccessFunction => refreshInsured(onSuccessFunction)}
        sideBarTitle={insured?.insuredCompanyName || ''}
        onClose={onClose}
      />
    </>
  );
};

InsuredProfileHeader.propTypes = { insuredId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

export default InsuredProfileHeader;
